import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import {
  Search, Home, TrendingUp, MapPin, DollarSign, ShieldCheck,
  Calculator, KanbanSquare, Sparkles, ArrowRight, Heart, Zap,
  Star, Check, X as XIcon, FileText, User, Building, Bed, Bath, Ruler
} from "lucide-react";

const PROP_IMG = {
  penthouse: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=900&q=85",
  loft: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900&q=85",
  jardin: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=900&q=85",
  casa: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=85",
  villa: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=900&q=85",
  depto: "https://images.unsplash.com/photo-1574362848149-11496d93a7c7?w=900&q=85",
};

const ECOSYSTEM = [
  {
    id: "matchmaking",
    name: "Matchmaking",
    icon: Search,
    desc: "Conecta propiedades con compradores ideales mediante algoritmos de compatibilidad.",
    group: "core",
  },
  {
    id: "portal",
    name: "Portal de Propiedades",
    icon: Home,
    desc: "Publica hasta 50 propiedades y destaca 5 con visibilidad premium.",
    group: "core",
  },
  {
    id: "mvip",
    name: "Estimación de Valor",
    icon: TrendingUp,
    desc: "Valuaciones inmobiliarias con factores Ross-Heidecke y metodología MVIP.",
    group: "core",
  },
  {
    id: "terreno",
    name: "Potencial de Terreno",
    icon: MapPin,
    desc: "Analiza uso de suelo, densidad constructiva y rentabilidad de cualquier lote.",
    group: "core",
  },
  {
    id: "rentas",
    name: "Rentas por Matchmaking",
    icon: DollarSign,
    desc: "Calcula rentas óptimas y conecta con inquilinos compatibles.",
    group: "pro",
  },
  {
    id: "inquilinos",
    name: "Investigación de Inquilinos",
    icon: ShieldCheck,
    desc: "Perfila y valida inquilinos potenciales antes de firmar contrato.",
    group: "pro",
  },
  {
    id: "notarial",
    name: "Cálculo Notarial",
    icon: Calculator,
    desc: "Estima honorarios, impuestos y costos de escrituración.",
    group: "pro",
  },
  {
    id: "crm",
    name: "CRM",
    icon: KanbanSquare,
    desc: "Gestiona relaciones con clientes y optimiza interacciones.",
    group: "pro",
  },
];

// ============= DEMO COMPONENTS =============

const RegisterCTA = ({ onClick, label = "¿Te interesa esta propiedad? Regístrate para ver más detalles y hacer match." }) => (
  <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-[#CCD500]/10 to-transparent border border-[#CCD500]/30 flex flex-col sm:flex-row items-center gap-3">
    <p className="text-sm text-slate-200 flex-1 text-center sm:text-left">{label}</p>
    <Button
      onClick={onClick}
      data-testid="ecosystem-register-btn"
      className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full px-5 h-10 shrink-0"
    >
      <Sparkles className="w-4 h-4 mr-1.5" />
      Registrarme
    </Button>
  </div>
);

const MatchmakingDemo = ({ navigate, isRental = false }) => (
  <div data-testid="demo-matchmaking">
    <div className="relative rounded-2xl overflow-hidden bg-slate-900 border border-white/10">
      <div className="relative h-56 md:h-72">
        <img src={PROP_IMG.penthouse} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="bg-[#CCD500] text-black px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Heart className="w-3.5 h-3.5 fill-black" /> 93% Match
          </span>
          {isRental && (
            <span className="bg-fuchsia-500 text-white px-3 py-1 rounded-full text-xs font-bold">RENTA</span>
          )}
        </div>
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="font-display text-2xl md:text-3xl font-black text-white">Penthouse con Vista Panorámica</h3>
          <p className="text-sm text-slate-300 flex items-center gap-1.5 mt-1">
            <MapPin className="w-3.5 h-3.5" /> Polanco, CDMX
          </p>
        </div>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-4 gap-2 text-center">
          {[
            { i: Bed, v: "4", l: "Recámaras" },
            { i: Bath, v: "3", l: "Baños" },
            { i: Ruler, v: "300", l: "m²" },
            { i: DollarSign, v: isRental ? "$65K/m" : "$18.0M", l: isRental ? "Renta MXN" : "Precio MXN" },
          ].map((f) => (
            <div key={f.l} className="p-2 rounded-lg bg-white/5">
              <f.i className="w-4 h-4 text-[#CCD500] mx-auto mb-1" />
              <p className="font-display text-sm font-bold text-white">{f.v}</p>
              <p className="text-[10px] text-slate-400 uppercase">{f.l}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4">
          {[
            { n: "Emocional", v: 89, c: "bg-rose-500" },
            { n: "Técnico", v: 86, c: "bg-blue-500" },
            { n: "Urbano", v: 72, c: "bg-emerald-500" },
            { n: "Financiero", v: 72, c: "bg-amber-500" },
          ].map((p) => (
            <div key={p.n} className="text-center">
              <div className={`w-12 h-12 rounded-full ${p.c} mx-auto flex items-center justify-center font-display text-sm font-black text-white`}>
                {p.v}%
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 uppercase">{p.n}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4">
          {["Luz de Tarde", "Wow Factor", "Social Host", isRental ? "Amueblado" : "Pet Friendly"].map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
    <RegisterCTA onClick={() => navigate("/comprador")} />
  </div>
);

const PortalDemo = ({ navigate }) => {
  const listings = [
    { img: PROP_IMG.penthouse, t: "Penthouse Polanco", c: "Polanco, CDMX", p: "$18.0M", s: 93 },
    { img: PROP_IMG.loft, t: "Loft Industrial", c: "Roma Norte, CDMX", p: "$8.5M", s: 87 },
    { img: PROP_IMG.jardin, t: "Casa Zen con Jardín", c: "Coyoacán, CDMX", p: "$12.0M", s: 91 },
    { img: PROP_IMG.casa, t: "Casa Moderna", c: "San Pedro, GDL", p: "$15.5M", s: 84 },
    { img: PROP_IMG.villa, t: "Villa Frente al Mar", c: "Cancún, QR", p: "$24.0M", s: 89 },
    { img: PROP_IMG.depto, t: "Depto Lujo", c: "Mérida, YUC", p: "$6.8M", s: 75 },
  ];
  return (
    <div data-testid="demo-portal">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {listings.map((l, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="relative rounded-xl overflow-hidden bg-slate-900 border border-white/10 group cursor-pointer hover:border-[#CCD500]/40 transition-all"
          >
            <div className="relative h-28">
              <img src={l.img} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
              <span className="absolute top-2 right-2 bg-[#CCD500] text-black px-2 py-0.5 rounded-full text-[10px] font-bold">
                {l.s}%
              </span>
            </div>
            <div className="p-2.5">
              <p className="text-xs font-bold text-white truncate">{l.t}</p>
              <p className="text-[10px] text-slate-400 truncate">{l.c}</p>
              <p className="text-xs font-display font-black text-[#CCD500] mt-1">{l.p} MXN</p>
            </div>
          </motion.div>
        ))}
      </div>
      <RegisterCTA onClick={() => navigate("/comprador")} />
    </div>
  );
};

const MockField = ({ label, value, full = false }) => (
  <div className={full ? "col-span-2" : ""}>
    <p className="text-xs text-slate-400 mb-1">{label}</p>
    <div className="px-3 py-2 rounded-lg bg-slate-950/80 border border-white/10 text-sm text-white truncate">{value}</div>
  </div>
);

const MVIPDemo = ({ navigate }) => (
  <div data-testid="demo-mvip">
    <div className="rounded-2xl bg-slate-900 border border-white/10 p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-widest text-[#CCD500] font-bold">Paso 1: Datos del Inmueble</p>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${n === 1 ? "bg-[#CCD500] text-black" : "bg-white/5 text-slate-500"}`}>
              {n}
            </span>
          ))}
        </div>
      </div>
      <div className="h-1 rounded-full bg-white/5 mb-5 overflow-hidden">
        <div className="h-full w-1/5 bg-gradient-to-r from-[#CCD500] to-[#E5FF00]" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <MockField label="Descripción" value="Depto. lujo en torre residencial con amenidades" full />
        <MockField label="Ciudad/Municipio" value="Ciudad de México" />
        <MockField label="Colonia/Zona" value="Polanco" />
        <MockField label="Tipo de Propiedad" value="Departamento" />
        <MockField label="Superficie Construida" value="120 m²" />
        <MockField label="Antigüedad" value="10 años" />
        <MockField label="Valor Catastral" value="$1,500,000 MXN" />
      </div>
      <div className="mt-4">
        <p className="text-xs text-slate-400 mb-2">Estado de Conservación: <span className="text-white font-semibold">Regular</span></p>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={`w-9 h-7 rounded-md flex items-center justify-center text-xs ${n <= 3 ? "bg-[#CCD500] text-black" : "bg-white/5 text-slate-500"}`}>
              <Star className={`w-3.5 h-3.5 ${n <= 3 ? "fill-black" : ""}`} />
            </span>
          ))}
        </div>
      </div>
      <div className="mt-5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
        <Zap className="w-4 h-4 text-emerald-400" />
        <p className="text-xs text-emerald-300">IA con Claude Sonnet generará dictamen técnico + PDF firmado en menos de 30s.</p>
      </div>
    </div>
    <Button
      onClick={() => navigate("/auth")}
      className="w-full mt-4 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-11"
      data-testid="demo-mvip-cta"
    >
      Activar Valuación MVIP <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  </div>
);

const TerrenoDemo = ({ navigate }) => (
  <div data-testid="demo-terreno">
    <div className="rounded-2xl bg-slate-900 border border-white/10 p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-widest text-[#CCD500] font-bold">Paso 1: Datos del Terreno</p>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${n === 1 ? "bg-[#CCD500] text-black" : "bg-white/5 text-slate-500"}`}>
              {n}
            </span>
          ))}
        </div>
      </div>
      <div className="h-1 rounded-full bg-white/5 mb-5 overflow-hidden">
        <div className="h-full w-1/4 bg-gradient-to-r from-[#CCD500] to-[#E5FF00]" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <MockField label="Referencia / Dirección" value="Av. Reforma 1200, Polanco" full />
        <MockField label="Superficie total" value="500 m²" />
        <MockField label="Frente lineal" value="15 ml" />
        <MockField label="Niveles permitidos" value="2" />
        <MockField label="Topografía" value="Plano" />
        <MockField label="Precio de venta" value="$12,000,000 MXN" />
        <MockField label="Costo construcción" value="$12,000/m²" />
        <MockField label="Zona / Colonia" value="Polanco" />
        <MockField label="Uso de suelo" value="H4 / Mixto" />
        <MockField label="Usos permitidos" value="Habitacional, Mixto, Comercial" full />
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {["Casa Unifamiliar", "Edificio Depts.", "Comercial"].map((s, i) => (
          <div key={s} className={`p-2.5 rounded-lg text-center ${i === 1 ? "bg-[#CCD500]/10 border border-[#CCD500]/40" : "bg-white/5 border border-white/5"}`}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s}</p>
            <p className={`font-display text-base font-black ${i === 1 ? "text-[#CCD500]" : "text-white"}`}>
              ROI {[18, 34, 22][i]}%
            </p>
          </div>
        ))}
      </div>
    </div>
    <Button
      onClick={() => navigate("/auth")}
      className="w-full mt-4 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-11"
      data-testid="demo-terreno-cta"
    >
      Activar Potencial de Terreno <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  </div>
);

const TenantDemo = ({ navigate }) => {
  const checks = [
    { label: "Identidad verificada", ok: true },
    { label: "Comprobante de ingresos", ok: true },
    { label: "Historial buró de crédito", ok: true },
    { label: "Referencias arrendaticias", ok: true },
    { label: "Antecedentes penales", ok: false },
    { label: "Score de riesgo financiero", ok: true },
  ];
  return (
    <div data-testid="demo-inquilinos">
      <div className="rounded-2xl bg-slate-900 border border-white/10 p-5">
        <div className="flex items-center gap-4 pb-4 border-b border-white/10">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#CCD500] to-emerald-500 flex items-center justify-center font-display text-xl font-black text-black">
            JG
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-white">Juan García Méndez</h3>
            <p className="text-xs text-slate-400">31 años · Profesionista · CDMX</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Score</p>
            <p className="font-display text-3xl font-black text-emerald-400">847</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mt-4">
          {checks.map((c) => (
            <div key={c.label} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5">
              {c.ok ? (
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : (
                <XIcon className="w-4 h-4 text-amber-400 shrink-0" />
              )}
              <p className="text-xs text-slate-200">{c.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <p className="text-xs font-bold text-emerald-300 uppercase tracking-wider">Recomendación</p>
          </div>
          <p className="text-sm text-slate-200">
            <span className="font-bold text-emerald-400">Inquilino aprobado.</span> Bajo riesgo financiero. Procede con contrato.
          </p>
        </div>
      </div>
      <Button
        onClick={() => navigate("/auth")}
        className="w-full mt-4 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-11"
        data-testid="demo-inquilinos-cta"
      >
        Investigar a un inquilino <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

const NotaryDemo = ({ navigate }) => {
  const items = [
    { label: "ISAI (3% sobre valor)", value: 360000 },
    { label: "Honorarios notariales (1.2%)", value: 144000 },
    { label: "IVA sobre honorarios (16%)", value: 23040 },
    { label: "Inscripción en Registro Público (0.35%)", value: 42000 },
    { label: "ISR estimado (vendedor)", value: 180000 },
    { label: "Gestoría y avalúo", value: 10000 },
  ];
  const total = items.reduce((s, i) => s + i.value, 0);
  const valor = 12000000;
  return (
    <div data-testid="demo-notarial">
      <div className="rounded-2xl bg-slate-900 border border-white/10 p-5">
        <div className="flex items-start justify-between pb-4 border-b border-white/10">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#CCD500] font-bold">Operación de compraventa</p>
            <p className="font-display text-lg font-bold text-white mt-1">Penthouse Polanco</p>
            <p className="text-xs text-slate-400">Valor: ${valor.toLocaleString("es-MX")} MXN</p>
          </div>
          <Calculator className="w-8 h-8 text-[#CCD500]" />
        </div>
        <div className="space-y-2 mt-4">
          {items.map((it) => (
            <div key={it.label} className="flex justify-between items-center text-sm">
              <span className="text-slate-300">{it.label}</span>
              <span className="font-mono text-white">${it.value.toLocaleString("es-MX")}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
          <span className="text-xs uppercase tracking-widest text-slate-400">Total estimado</span>
          <span className="font-display text-2xl font-black text-[#CCD500]">${total.toLocaleString("es-MX")}</span>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          * Costos referenciales CDMX. Valores exactos pueden variar según notaría y aranceles vigentes.
        </p>
      </div>
      <Button
        onClick={() => navigate("/auth")}
        className="w-full mt-4 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-11"
        data-testid="demo-notarial-cta"
      >
        Calcular mi operación <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

const CRMDemo = ({ navigate }) => {
  const cols = [
    { name: "Fase 1: Blind Match", color: "bg-rose-500", icon: Heart, count: 28, items: ["Casa Zen con Jardín en Coyoacán", "Loft Industrial Roma Norte", "Villa Frente al Mar en Cancún"] },
    { name: "Fase 2: Intent/Contrato", color: "bg-orange-500", icon: FileText, count: 3, items: ["Loft Industrial · pending signatures", "Casa Zen · pending signatures"] },
    { name: "Fase 3: Reveal", color: "bg-emerald-500", icon: Check, count: 1, items: ["Penthouse con Vista · signed"] },
    { name: "Fase 4: Visita", color: "bg-purple-500", icon: User, count: 4, items: ["Casa Moderna · agendada", "Villa Mar · agendada"] },
  ];
  return (
    <div data-testid="demo-crm">
      <div className="rounded-2xl bg-slate-900 border border-white/10 p-5 overflow-x-auto">
        <div className="flex gap-3 min-w-[600px]">
          {cols.map((c) => (
            <div key={c.name} className="flex-1 min-w-[160px]">
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-6 h-6 rounded-full ${c.color} flex items-center justify-center`}>
                  <c.icon className="w-3 h-3 text-white" />
                </span>
                <p className="text-xs font-bold text-white flex-1">{c.name}</p>
                <span className="px-1.5 py-0.5 rounded-md bg-white/10 text-[10px] font-bold text-white">{c.count}</span>
              </div>
              <div className="space-y-2">
                {c.items.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="p-2.5 rounded-lg bg-slate-950 border border-white/5 text-[11px] text-slate-200 leading-snug"
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Button
        onClick={() => navigate("/auth")}
        className="w-full mt-4 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-11"
        data-testid="demo-crm-cta"
      >
        Activar mi CRM <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

const DEMOS = {
  matchmaking: (nav) => <MatchmakingDemo navigate={nav} />,
  portal: (nav) => <PortalDemo navigate={nav} />,
  mvip: (nav) => <MVIPDemo navigate={nav} />,
  terreno: (nav) => <TerrenoDemo navigate={nav} />,
  rentas: (nav) => <MatchmakingDemo navigate={nav} isRental />,
  inquilinos: (nav) => <TenantDemo navigate={nav} />,
  notarial: (nav) => <NotaryDemo navigate={nav} />,
  crm: (nav) => <CRMDemo navigate={nav} />,
};

// ============= MAIN SECTION =============

export const EcosystemSection = () => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  const current = ECOSYSTEM.find((m) => m.id === openId);

  return (
    <section id="ecosystem" className="relative py-24 md:py-32 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
            <span className="h-px w-8 bg-[#CCD500]/60" /> Ecosistema Digital Completo
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
            8 herramientas. <span className="text-[#CCD500]">Una sola plataforma.</span>
          </h2>
          <p className="mt-4 text-slate-400">
            Toca cualquier módulo para ver el demo interior. Toda la operación inmobiliaria — compra, renta, valuación, terreno, inquilinos, notarial y CRM — integrada en Findperty.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {ECOSYSTEM.map((m, i) => (
            <motion.button
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -4 }}
              onClick={() => setOpenId(m.id)}
              data-testid={`ecosystem-card-${m.id}`}
              className="group text-left relative rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-5 md:p-6 hover:border-[#CCD500]/40 transition-all overflow-hidden"
            >
              <div className={`absolute inset-x-0 top-0 h-0.5 ${m.group === "core" ? "bg-[#CCD500]" : "bg-orange-400"}`} />
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${m.group === "core" ? "bg-[#CCD500]/10 border border-[#CCD500]/30" : "bg-orange-500/10 border border-orange-500/30"}`}>
                <m.icon className={`w-6 h-6 ${m.group === "core" ? "text-[#CCD500]" : "text-orange-400"}`} />
              </div>
              <h3 className="font-display text-base md:text-lg font-bold text-white leading-tight">{m.name}</h3>
              <p className="text-xs text-slate-400 mt-2 leading-snug">{m.desc}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#CCD500] group-hover:gap-2 transition-all">
                Ver demo <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent
          className="max-w-3xl bg-[#0B1120] border border-white/10 text-slate-100 max-h-[92vh] overflow-y-auto p-0"
          data-testid="ecosystem-dialog"
        >
          {current && (
            <>
              <div className="sticky top-0 z-10 bg-[#0B1120]/95 backdrop-blur-md border-b border-white/10 p-5 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${current.group === "core" ? "bg-[#CCD500]/10 border border-[#CCD500]/30" : "bg-orange-500/10 border border-orange-500/30"}`}>
                  <current.icon className={`w-5 h-5 ${current.group === "core" ? "text-[#CCD500]" : "text-orange-400"}`} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">Demo interactivo</p>
                  <h3 className="font-display text-xl font-bold text-white leading-tight">{current.name}</h3>
                </div>
              </div>
              <div className="p-5 md:p-6">{DEMOS[current.id]?.(navigate)}</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default EcosystemSection;
